import { useEffect, useState } from 'react';
import { documentPartyLabels, formatFileSize, type DocumentParty } from '@brimo/shared';
import { Banner, Button, Checkbox, Dialog, FileField, Select, TextField } from '../../../ds/components';
import { ApiError, apiUpload, errorMessage } from '../../../lib/api';
import { useDocumentTypes } from '../api';
import type { DocumentRow } from '../types';
import styles from '../dialogs.module.css';

export interface UploadDialogProps {
  open: boolean;
  contactId: string;
  onClose: () => void;
  onUploaded: (row: DocumentRow) => void;
}

const PARTIES: DocumentParty[] = ['customer', 'broker', 'financier', 'internal'];

/** „Hochladen" of the document browser: one PDF/JPG/PNG file with party, type and customer visibility (`POST /documents`). */
export function UploadDialog({ open, contactId, onClose, onUploaded }: UploadDialogProps) {
  const types = useDocumentTypes(open);
  const [file, setFile] = useState<File | null>(null);
  const [party, setParty] = useState<DocumentParty>('customer');
  const [typeId, setTypeId] = useState('');
  const [name, setName] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setParty('customer');
    setTypeId('');
    setName('');
    setVisible(false);
    setError(null);
    setFileError(undefined);
  }, [open]);

  async function submit() {
    if (!file) {
      setFileError('Bitte eine Datei hochladen.');
      return;
    }
    setFileError(undefined);
    setError(null);
    setPending(true);
    try {
      const form = new FormData();
      form.set('contactId', contactId);
      form.set('party', party);
      if (typeId) form.set('documentTypeId', typeId);
      if (name.trim()) form.set('name', name.trim());
      form.set('visibleToCustomer', visible ? 'true' : 'false');
      form.set('file', file, file.name);
      const row = await apiUpload<DocumentRow>('/documents', form);
      onUploaded(row);
    } catch (err) {
      if (err instanceof ApiError && err.fields['file']) setFileError(err.fields['file']);
      else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Dokumente"
      title="Dokument hochladen"
      subtitle="PDF, JPG oder PNG bis 10 MB. Die Datei wird vor der Freigabe auf Viren geprüft."
      width={600}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            Hochladen
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <FileField
          label="Datei"
          title="Datei hierher ziehen"
          description="PDF, JPG oder PNG."
          buttonLabel="Datei auswählen"
          accept="application/pdf,image/jpeg,image/png"
          error={fileError}
          onFiles={(files) => {
            const [first] = files;
            setFile(first ?? null);
            setFileError(undefined);
          }}
          items={file ? [{ id: 'file', name: file.name, size: formatFileSize(file.size) }] : []}
          onRemove={() => setFile(null)}
        />
        <div className={styles.twoColumns}>
          <Select
            label="Partei"
            value={party}
            options={PARTIES.map((key) => ({ value: key, label: documentPartyLabels[key] }))}
            onChange={(event) => setParty(event.target.value as DocumentParty)}
          />
          <Select
            label="Dokumenttyp"
            optional
            placeholder="Ohne Typ"
            value={typeId}
            options={(types.data?.items ?? []).map((type) => ({ value: type.id, label: type.name }))}
            onChange={(event) => setTypeId(event.target.value)}
          />
        </div>
        <TextField
          label="Name"
          optional
          hint="Ohne Angabe gilt der Dateiname."
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Checkbox
          label="Für den Kunden im Portal sichtbar"
          checked={visible}
          onChange={(event) => setVisible(event.target.checked)}
        />
      </div>
    </Dialog>
  );
}
